import { useCallback, useState } from "react";
import { useTelemetryNotesManager } from "@/hooks/useTelemetryNotes";
import type { TelemetryNote } from "@/types";

/**
 * UI state for telemetry notes on the analytics page: notes panel visibility,
 * note modal, notes-enabled toggle, and the modal-opening handlers that pair
 * a notes-manager action with the modal state.
 */
export function useNotesUi(
  notesManager: ReturnType<typeof useTelemetryNotesManager>,
): {
  showNotesPanel: boolean;
  setShowNotesPanel: (show: boolean) => void;
  isNoteModalOpen: boolean;
  notesEnabled: boolean;
  setNotesEnabled: (enabled: boolean) => void;
  handleNoteCreate: (timeOffset: number) => void;
  handleNoteClick: (note: TelemetryNote) => void;
  handleModalClose: () => void;
  handleCloseNotesPanel: () => void;
} {
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [notesEnabled, setNotesEnabled] = useState(false);

  const handleNoteCreate = useCallback(
    (timeOffset: number) => {
      notesManager.startCreatingNote(timeOffset);
      setIsNoteModalOpen(true);
    },
    [notesManager],
  );

  const handleNoteClick = useCallback(
    (note: TelemetryNote) => {
      notesManager.selectNote(note);
      setIsNoteModalOpen(true);
    },
    [notesManager],
  );

  const handleModalClose = useCallback(() => {
    setIsNoteModalOpen(false);
    notesManager.cancelCreatingNote();
    notesManager.selectNote(null);
  }, [notesManager]);

  const handleCloseNotesPanel = useCallback(
    () => setShowNotesPanel(false),
    [],
  );

  return {
    showNotesPanel,
    setShowNotesPanel,
    isNoteModalOpen,
    notesEnabled,
    setNotesEnabled,
    handleNoteCreate,
    handleNoteClick,
    handleModalClose,
    handleCloseNotesPanel,
  };
}
